import parsePhoneNumber, { parsePhoneNumberFromString } from 'libphonenumber-js';

import Amex from '/public/icons/credit-cards/amex.png';
import { AxiosError } from 'axios';
import { CreditCardType } from '../services/credit-cards/models';
import Mada from '/public/icons/credit-cards/mada.png';
import Mastercard from '/public/icons/credit-cards/mastercard.png';

import { SHA512 } from 'crypto-js';
import { ValidationCreditCardType } from '../hooks';
import Visa from '/public/icons/credit-cards/visa.png';

const EmptyUpComing = '/icons/my-booking/emptyUpComing.svg';
const PlaceholderCardIcon = '/icons/credit-cards/placeholder-card.svg';

/* Defining an interface called country. */
export interface country {
  countryCode: string;
  dialCode: string;
  name: string;
}

/**
 * This TypeScript function returns the image source of a credit card icon based on its type.
 * @param {CreditCardType} type - Credit card type, which is an enum with possible values of Visa,
 * MasterCard, and Amex.
 * @returns A string representing the source of the icon for a given credit card type. If the type is
 * not recognized, a default icon source is returned.
 */
export const getCreditCardIcon = (type: CreditCardType): string => {
  if (type === CreditCardType.Visa) return Visa.src;
  else if (type === CreditCardType.MasterCard) return Mastercard.src;
  else if (type === CreditCardType.Amex) return Amex.src;
  else if (type === CreditCardType.Mada) return Mada.src;
  else return EmptyUpComing;
};

/**
 * This TypeScript function returns a credit card icon based on the type of credit card entered.
 * @param {ValidationCreditCardType} type - ValidationCreditCardType, which is a string representing
 * the type of credit card (e.g. 'visa', 'mastercard', 'american-express').
 * @returns The function `getInputCreditCardIcon` returns a string which is the icon of the credit card
 * type passed as an argument. If the argument is not one of the supported credit card types, it
 * returns a placeholder card icon.
 */
export const getInputCreditCardIcon = (type: ValidationCreditCardType): string => {
  if (type === 'visa') return getCreditCardIcon(CreditCardType.Visa);
  else if (type === 'mastercard') return getCreditCardIcon(CreditCardType.MasterCard);
  else if (type === 'american-express') return getCreditCardIcon(CreditCardType.Amex);
  else if (type === 'mada') return getCreditCardIcon(CreditCardType.Mada);
  else return PlaceholderCardIcon;
};

/**
 * It takes a phone number and a dial code, checks if the first number is equal to '0' and removes it,
 * removes all non-digits chars, and returns the phone number with the dial code.
 * @param {string} phoneNumber - string - the phone number that you want to trim
 * @param {string} dialCode - "+91"
 * @returns A function that takes two parameters, phoneNumber and dialCode.
 */
export const muviPhoneNumberTrimmer = (phoneNumber: string, dialCode: string): string => {
  /* Removing the dial code from the phone number. */
  phoneNumber = phoneNumber?.substring(dialCode.length + 1);
  /* Checking if the first number is equal to '0' and removing it. */
  if (phoneNumber.charAt(0) === '0') {
    phoneNumber = phoneNumber?.substring(1);
  }
  /* Removing all non-digits chars. */
  phoneNumber = phoneNumber?.replace(/\D/g, '');
  /* Concatenating the dialCode and the phoneNumber. */
  phoneNumber = `+${dialCode}${phoneNumber}`;
  return phoneNumber;
};
export const getDialCodeFromPhoneNumber = (phoneNumber: string): string => {
  // Parse the phone number
  const parsedNumber = parsePhoneNumberFromString(phoneNumber);

  // Extract the dial code
  const dialCode = parsedNumber.countryCallingCode;

  return dialCode;
};

export const is500 = (error: unknown): boolean => {
  return (
    (error as AxiosError).response?.status >= 500 &&
    (error as AxiosError).response?.status !== 503
  );
};

export const handleError = (
  error: AxiosError
): {
  redirect: {
    destination: string;
    permanent: boolean;
  };
} => {
  if (error?.response?.status >= 500)
    return {
      redirect: {
        destination: '500',
        permanent: false,
      },
    };
};

export const getUserAgent = (): string => window.navigator.userAgent;

export const getDeviceTimeZone = (): number => new Date().getTimezoneOffset();

export const getBrowserWidth = (): number => {
  return Math.max(
    document.body.scrollWidth,
    document.documentElement.scrollWidth,
    document.body.offsetWidth,
    document.documentElement.offsetWidth,
    document.documentElement.clientWidth
  );
};

export const getBrowserHeight = (): number => {
  return Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.documentElement.clientHeight
  );
};

export const checkIfPhoneNumberIsValid = (phoneNumber: string, countryCode): boolean => {
  const _phoneNumber = parsePhoneNumber(phoneNumber?.toString(), countryCode);
  return _phoneNumber?.isValid() === true;
};

/**
 * It replaces each Arabic digit in the input string with its equivalent in the string '٠١٢٣٤٥٦٧٨٩'
 */
export const aN2eN = (s): string => s.replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));

export const hashString = inputString => {
  return SHA512(inputString).toString();
};
export const sliceStringWithoutSpaces = (str: string, number?: number) => {
  return str.replace(/ /g, '').substring(0, number || 8);
};
export const acceptOnlyNumberOnSafari = (e: React.KeyboardEvent<HTMLDivElement>) => {
  if (
    e &&
    !/[0-9]/.test(e.key) && // Allow only numbers
    e.key !== 'Backspace' && // Allow backspace
    e.key !== 'Tab' && // Allow tab
    e.key !== 'ArrowLeft' && // Allow left arrow
    e.key !== 'ArrowRight' && // Allow right arrow
    e.key !== 'Delete' && // Allow delete
    e.key !== 'Enter' && // Allow enter
    e.key !== 'Escape' // Allow escape
  ) {
    e.preventDefault();
  }
};
